import AwsSssm, {
  GetParameterRequest,
  GetParametersRequest,
} from 'aws-sdk/clients/ssm';
import { ReebeloStoreT, SSMBucket } from '@lambda/reebelo';
import LRUCache from 'lru-cache';
import { Logger } from '../../logging/logger';

const ssm = new AwsSssm();

const getSSMClientForRegion = (region: string) => new AwsSssm({ region });

const lruCache = new LRUCache<string, any>({
  max: 500,
  ttl: 1000 * 60 * 5,
});

const racingConditionResolve = (cacheKey: string): Promise<void> =>
  new Promise((resolve) => {
    let count = 0;

    const checkCache = () => {
      if (!lruCache.has(cacheKey) || count >= 10) {
        resolve();
      } else {
        count += 1;
        setTimeout(checkCache, 100);
      }
    };

    checkCache();
  });

export default class SSM {
  logger: Logger;

  constructor() {
    this.logger = new Logger('ssm');
  }

  async getValue<T>(
    key: string,
    defaultValue = null,
    cacheEnabled = true,
    ttl = 300000,
  ): Promise<T | null> {
    try {
      const name = `${key}`.toLowerCase();
      const racingKey = `racing: ${name}`;

      this.logger.info({ key, name }, 'getting SSM parameter');

      if (process.env[key]) {
        this.logger.warn({ key, name }, 'returning local env variable');

        return process.env[key] as T;
      }

      if (cacheEnabled) {
        if (lruCache.has(name)) {
          this.logger.info(`Config getValue hit cache: ${name}`);

          return lruCache.get(name) ?? null;
        }

        await racingConditionResolve(racingKey);

        if (lruCache.has(key)) {
          this.logger.info(`Config getValue hit cache after racing: ${name}`);

          return lruCache.get(key) ?? null;
        }

        lruCache.set(racingKey, 1, { ttl: 1000 });
      }

      const params: GetParameterRequest = {
        Name: name,
        WithDecryption: true,
      };

      const response = await ssm.getParameter(params).promise();

      if (response.Parameter && response.Parameter.Value) {
        if (cacheEnabled) {
          this.logger.info(`Config getValue set cache: ${name}`);
          lruCache.set(key, response.Parameter.Value, { ttl });
          lruCache.delete(racingKey);
        }

        return response.Parameter.Value as T;
      }

      throw new Error(`Parameter "${name}" not found`);
    } catch (e: any) {
      this.logger.error({ e, key }, 'error getting SSM parameter');

      return defaultValue;
    }
  }

  async getStoreValue<T>(
    store: SSMBucket,
    key: string,
    defaultValue = null,
    cacheEnabled = true,
    ttl = 300000,
  ): Promise<T | null> {
    const name = `/${store}/${key}`.toLowerCase();

    return this.getValue(name, defaultValue, cacheEnabled, ttl);
  }

  async getStoreValues<T>(
    bucket: SSMBucket,
    keys: string[],
  ): Promise<{ [key: string]: T } | null> {
    try {
      const names = keys.map((key) => `/${bucket}/${key}`.toLowerCase());

      const params: GetParametersRequest = {
        Names: names,
        WithDecryption: true,
      };

      const response = await ssm.getParameters(params).promise();
      const values: { [key: string]: T } = {};

      response.Parameters?.forEach((param) => {
        const paramName = param.Name?.split('/').pop();

        if (paramName) values[paramName] = param.Value as T;
      });

      if (Object.keys(values).length > 0) return values;

      throw new Error(`Parameters not found`);
    } catch (e: any) {
      this.logger.error({ e, keys, bucket }, 'error getting SSM parameters');

      return null;
    }
  }

  async getValueByRegion<T>(
    key: string,
    store: ReebeloStoreT,
    defaultValue = null,
    cacheEnabled = true,
    ttl = 300000,
  ): Promise<T | null> {
    try {
      const isNA = ['reebelo-us', 'reebelo-ca'].includes(store);
      const region = isNA ? 'us-west-1' : 'ap-southeast-1';
      const ssmClient = getSSMClientForRegion(region);
      const name = `${key}`.toLowerCase();
      const racingKey = `racing: ${name}`;

      this.logger.info({ key, name }, 'getting SSM parameter');

      if (process.env[key]) {
        this.logger.warn({ key, name }, 'returning local env variable');

        return process.env[key] as T;
      }

      if (cacheEnabled) {
        if (lruCache.has(name)) {
          this.logger.info(`Config getValue hit cache: ${name}`);

          return lruCache.get(name) ?? null;
        }

        await racingConditionResolve(racingKey);

        if (lruCache.has(key)) {
          this.logger.info(`Config getValue hit cache after racing: ${name}`);

          return lruCache.get(key) ?? null;
        }

        lruCache.set(racingKey, 1, { ttl: 1000 });
      }

      const params: GetParameterRequest = {
        Name: name,
        WithDecryption: true,
      };

      const response = await ssmClient.getParameter(params).promise();

      if (response.Parameter && response.Parameter.Value) {
        if (cacheEnabled) {
          this.logger.info(`Config getValue set cache: ${name}`);
          lruCache.set(key, response.Parameter.Value, { ttl });
          lruCache.delete(racingKey);
        }

        return response.Parameter.Value as T;
      }

      throw new Error(`Parameter "${name}" not found`);
    } catch (e: any) {
      this.logger.error({ e, key }, 'error getting SSM parameter');

      return defaultValue;
    }
  }
}
